import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, Button, ButtonGroup, Menu, MenuItem, ListItemIcon, ListItemText, IconButton } from "@mui/material";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { CloseRounded } from "@mui/icons-material";
import { useMetaKey } from "../../../../../../shell/hooks/useMetaKey";
import { ContentModel } from "../../../../../../shell/services/types";
import { ContentBreadcrumbs } from "../../components/ContentBreadcrumbs";
import { ActionAfterSave } from "./ItemCreate";
import { useParams } from "../../../../../../shell/hooks/useParams";
import { CreateContentItemDialogContext } from "../../../../../../shell/contexts/CreateContentItemDialogProvider";
type DropdownMenuType = "default" | "addNew";
// Maps each action to its i18n key; t() can't run at module scope so the
// label lookup happens inside the component.
const DropdownMenu: Record<DropdownMenuType, Record<string, string>> = {
  default: {
    publishNow: "content.itemCreatePublishNow",
    schedulePublish: "content.itemCreateSchedulePublish"
  },
  addNew: {
    publishAddNew: "content.itemCreatePublishAddNew",
    schedulePublishAddNew: "content.itemCreateSchedulePublishAddNew"
  }
};
interface Props {
  model: ContentModel;
  onSave: (action: ActionAfterSave) => void;
  isLoading: boolean;
  isDirty: boolean;
}
export const Header = ({
  model,
  onSave,
  isLoading,
  isDirty
}: Props) => {
  const {
    t
  } = useTranslation();
  const [params] = useParams();
  const [dropdownMenuType, setDropdownMenuType] = useState<DropdownMenuType | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null);
  const [_, setInitiatorZUID] = useContext(CreateContentItemDialogContext);
  const metaShortcut = useMetaKey("s", onSave);
  const isRenderedAsDialog = params.get("isDialog") === "true";
  return <>
      <Stack px={4} pt={4} pb={1.75} borderBottom="2px solid" borderColor="border" sx={{
      backgroundColor: "background.paper"
    }} direction="row" justifyContent="space-between" alignItems="flext-start" data-sentry-element="Stack" data-sentry-source-file="Header.tsx">
        <Stack gap={0.25} data-sentry-element="Stack" data-sentry-source-file="Header.tsx">
          {model?.type !== "block" && <ContentBreadcrumbs />}
          <Typography variant="h3" fontWeight={700} color="text.primary" sx={{
          display: "-webkit-box",
          WebkitLineClamp: "2",
          WebkitBoxOrient: "vertical",
          wordBreak: "break-word",
          wordWrap: "break-word",
          hyphens: "auto",
          overflow: "hidden"
        }} data-sentry-element="Typography" data-sentry-source-file="Header.tsx">
            {model.type === "block" ? t("content.itemCreateTitleVariant") : t("content.itemCreateTitleItem", {
            label: model.label
          })}
          </Typography>
        </Stack>
        <Stack direction="row" gap={1} flexShrink={0} alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="Header.tsx">
          {!isRenderedAsDialog && <ButtonGroup variant="outlined" color="primary" size="small" disabled={!isDirty || isLoading}>
              <Button startIcon={<AddRoundedIcon />} onClick={() => {
            onSave("addNew");
          }} loading={isLoading} variant="outlined">
                {t("content.itemCreateAddNew")}
              </Button>
              <Button size="xsmall" onClick={evt => {
            setAnchorEl(evt.currentTarget);
            setDropdownMenuType("addNew");
          }} sx={{
            px: 1,
            "&.MuiButtonGroup-grouped": {
              minWidth: 34
            }
          }}>
                <ArrowDropDownRoundedIcon sx={{
              fontSize: 18
            }} />
              </Button>
            </ButtonGroup>}
          <ButtonGroup variant="contained" color="primary" size="small" disabled={!isDirty || isLoading} sx={{
          "& .MuiButtonGroup-grouped": {
            backgroundColor: "primary.main",
            color: "common.white",
            "&:hover": {
              backgroundColor: "primary.dark"
            }
          }
        }} data-sentry-element="ButtonGroup" data-sentry-source-file="Header.tsx">
            <Button startIcon={<SaveRoundedIcon />} onClick={() => {
            onSave("");
          }} loading={isLoading} variant="contained" data-cy="CreateItemSaveButton" data-sentry-element="Button" data-sentry-source-file="Header.tsx">
              {t("common.create")}
            </Button>
            <Button size="xsmall" onClick={evt => {
            setAnchorEl(evt.currentTarget);
            setDropdownMenuType("default");
          }} sx={{
            px: 1,
            "&.MuiButtonGroup-grouped": {
              minWidth: 34
            }
          }} data-sentry-element="Button" data-sentry-source-file="Header.tsx">
              <ArrowDropDownRoundedIcon sx={{
              fontSize: 18
            }} data-sentry-element="ArrowDropDownRoundedIcon" data-sentry-source-file="Header.tsx" />
            </Button>
          </ButtonGroup>
          {isRenderedAsDialog && <IconButton size="small" onClick={() => {
          setInitiatorZUID(null);
        }}>
              <CloseRounded />
            </IconButton>}
        </Stack>
      </Stack>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => {
      setAnchorEl(null);
      setDropdownMenuType(null);
    }} anchorOrigin={{
      vertical: "bottom",
      horizontal: "right"
    }} transformOrigin={{
      vertical: "top",
      horizontal: "right"
    }} slotProps={{
      paper: {
        sx: {
          mt: 1
        }
      }
    }} data-sentry-element="Menu" data-sentry-source-file="Header.tsx">
        {!!DropdownMenu[dropdownMenuType] && Object.entries(DropdownMenu[dropdownMenuType])?.map(([key, value]) => <MenuItem key={key} onClick={() => {
        onSave(key as ActionAfterSave);
        setAnchorEl(null);
        setDropdownMenuType(null);
      }} sx={{
        height: 40,
        px: 2,
        py: 1
      }}>
                <ListItemIcon>
                  {["publishNow", "publishAddNew"].includes(key) ? <CloudUploadRoundedIcon /> : <CalendarTodayRoundedIcon />}
                </ListItemIcon>
                <ListItemText>{t(value)}</ListItemText>
              </MenuItem>)}
      </Menu>
    </>;
};